
import { useMutation, useQuery } from '@apollo/client';
import { DefaultButton, Stack, Text } from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { customStyles } from 'common/components/FooterActionBar/index.styles';
import { useCommonStyles } from 'common/styles';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import APPROVAL_TYPE_COMMON_DATA from '../../../../../../ApprovalSetupFlowCommonData.graphql';
import { validationSchema } from '../../FormView';
import { TestCaseFormContent } from '../../FormView/TestCaseFormContent';
import { ApprovalTestGroups_approvalTestGroups_nodes_approvalTestItems_nodes } from '../../TestGroupsModal/__generated__/ApprovalTestGroups';
import { TestCaseValues } from '../../types';
import { useStyles } from './index.styles';
import { TestItemDelete } from './TestItemDelete';

export const TestItemFormView: React.FC<{
  testItem: ApprovalTestGroups_approvalTestGroups_nodes_approvalTestItems_nodes;
  onDelete: () => void;
  approvalId: string;
}> = ({ testItem, onDelete, approvalId }) => {
  const mapToTestCaseValues = () => ({
    comment: testItem?.comment || null,
    businessUnitId: testItem?.businessUnitId || null,
    departmentId: testItem?.departmentId || null,
    totalAmount: testItem?.totalAmount || null,
    currencyId: testItem?.currencyId || null,
    companyPhaseTypeId: testItem?.companyPhaseTypeId || null,
    requesterUserProfileId: testItem?.requesterUserProfileId || null,
    userGroupId: testItem?.userGroupId || null,
    workgroupId: testItem?.workgroupId || null,
    transactionHintId: testItem?.transactionHintId || null,
    purchaseOrderTypeId: testItem?.purchaseOrderTypeId || null,
    tagId: testItem?.tagId || null,
    elapsedDays: testItem?.elapsedDays || null,
    approvalLevelValueId: testItem?.approvalLevelValueId || null,
    userRankId: testItem?.userRankId || null,
    rowSecurityId: testItem?.rowSecurityId || null,
    additionalBusinessUnitId: testItem?.additionalBusinessUnitId || null,
    additionalDepartmentId: testItem?.additionalDepartmentId || null,
    additionalAmount: testItem?.additionalAmount || null,
  });

  const { data: approvalTypes } = useQuery<ApprovalSetupFlowCommonData>(
    APPROVAL_TYPE_COMMON_DATA,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );
  const formMethods = useForm<TestCaseValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const styles = useStyles();
  const commonStyles = useCommonStyles();

  useEffect(() => {
    formMethods.reset(mapToTestCaseValues());
  }, [formMethods, testItem]);

  return (
    <FormProvider {...formMethods}>
      <Stack grow style={{ overflow: 'auto', paddingTop: 10 }}>
        <Stack
          grow
          horizontal
          horizontalAlign="space-between"
          className={styles.headerStyle}
        >
          <Stack
            horizontal
            tokens={{ childrenGap: 10, padding: '0px 0px 20px 18px' }}
          >
            <Text variant="xLarge">{'Test Item'}</Text>
            {testItem && (
              <Text variant="xLarge" className={commonStyles.colorThemePrimary}>
                {testItem.comment}
              </Text>
            )}
          </Stack>
          <Stack className={styles.deleteIconStyle}>
            {testItem._rowTimestamp && (
              <TestItemDelete
                id={testItem.id}
                rowTimestamp={testItem._rowTimestamp}
                onClose={() => {
                  onDelete();
                }}
                approvalId={approvalId}
              />
            )}
          </Stack>
        </Stack>
        <TestCaseFormContent approvalTypes={approvalTypes} disabled />
      </Stack>
    </FormProvider>
  );
};
